<template>
  <li
    class="card-container"
    @mouseenter="handleMouseEnter"
    @mouseleave="isHover = false"
  >
    <Transition>
      <div
        v-if="!isHover"
        class="card-main"
      >
        <div class="card-svg">
          <img
            :alt="cardTitle"
            :src="cardSvg"
            :title="cardTitle"
            height="64"
            width="64"
          >
        </div>

        <h3 class="card-name">
          {{ cardTitle }}
        </h3>
        <span class="card-text">{{ cardText }}</span>
      </div>
    </Transition>
    <Transition>
      <div v-if="isHover">
        <a
          :href="linkUrl"
          class="card-main card-hover"
          rel="noopener noreferrer"
          target="_blank"
          @click="trackMainPageClick(trackName)"
        >
          <div
            v-loading="canvasLoading"
            class="card-svg"
          >
            <canvas
              v-if="cardTitle === 'iOS' || cardTitle === 'Android'"
              ref="qrCodeRef"
              height="104"
              width="104"
            />
            <img
              v-else
              :alt="cardTitle"
              :src="hoverSvg"
              :title="cardTitle"
            >
          </div>
          <h3 class="card-name hover">{{ hoverTitle }}</h3>
          <p
            v-show="hoverText"
            class="card-text"
          >{{ hoverText }}</p>
          <template v-if="hoverHyperlinks">
            <div class="hyperlink-box">
              <span
                class="hyperlink-item"
                :key="item.name"
                v-for="item in hoverHyperlinks"
                @click.stop="() => handleClickHyperlink(item)"
              >{{ item.name }}</span>
            </div>
          </template>
        </a>
      </div>
    </Transition>

    <Transition>
      <div
        v-if="topRightAngle && !isHover"
        class="card-top-right-angle"
      >
        <img
          :src="topRightAngle"
          alt=""
          class="card-top-right-angle-svg"
        >
      </div>
    </Transition>

    <div
      v-if="topRightLabel"
      class="card-top-right-label"
    >
      <img
        :src="TopRightLabelPNG"
        alt=""
      >
      <span class="card-top-right-label-content">{{ topRightLabel }}</span>
    </div>
  </li>
</template>

<script
  lang="ts"
  setup
>
import TopRightLabelPNG from '@/assets/images/home/top-right-label.png'
import $track from '@/track/webOfficialWebsite'
import QRCode from 'qrcode'
import { ref } from 'vue'

type TrackButtonNameType = Parameters<typeof $track.web_official_main_page_click>[0]['button'];

interface Hyperlink {
  name: string;
  url: string;
}

const props = defineProps<{
  cardTitle: string;
  cardSvg: string;
  cardText: string;
  topRightAngle?: string;
  topRightLabel?: string;
  hoverSvg?: string;
  hoverTitle?: string;
  hoverText?: string;
  hoverHyperlinks?: Hyperlink[];
  linkUrl?: string;
  trackName?: TrackButtonNameType;
  onClick?:() => void
}>()

const qrCodeRef = ref<HTMLCanvasElement>()
const canvasLoading = ref(false)
const isHover = ref(false)

const handleClickHyperlink = (hyperlink: Hyperlink) => {
  if (hyperlink.url) {
    window.open(hyperlink.url)
  }
}

const renderQrCode = async () => {
  if (!props.hoverSvg) return
  try {
    canvasLoading.value = true
    await QRCode.toCanvas(qrCodeRef.value, props.hoverSvg, { width: 104, color: { light: '#f2f5fa' } })
  } catch (err) {
    console.error('[renderQrCode: ]', err)
  }
  canvasLoading.value = false
}

const handleMouseEnter = async () => {
  isHover.value = props.hoverTitle !== undefined
  await renderQrCode()
}

const trackMainPageClick = (type?: TrackButtonNameType) => {
  if (!type) return
  props.onClick && props.onClick()
  $track.web_official_main_page_click({ button: type })
}
</script>

<style
  lang="less"
  scoped
>
.card {
  &-container {
    position: relative;
    width: 450px;
    background: #f2f5fa;
    border-radius: 20px;
    max-height: 250px;
    padding: 0 8px;
    overflow: hidden;

    .hyperlink-box {
      display: flex;
      gap: 12px;

      .hyperlink-item {
        font-size: 16px;
        line-height: 22px;
        color: #666;
        text-decoration: underline
      }
    }
  }

  &-main {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none; /* 去除默认的下划线 */
    outline: none; /* 去除旧版浏览器的点击后的外虚线框 */
    text-align: center;
    padding-bottom: 44px;

    #qr-code__content {
      margin-top: 30px;
      width: 104px;
      height: 104px;
    }
  }

  &-svg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 104px;
    height: 104px;
    margin-bottom: 8px;
    margin-top: 30px;
  }

  &-name {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;
    line-height: 34px;
    color: #000;

    &.hover {
      color: #306eff
    }
  }

  &-text {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #666;
    flex-wrap: nowrap;
    padding: 0 24px;
  }

  &-top-right-angle {
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border: 49px solid;
    border-radius: 0 20px 0 0;
    border-color: #e4e8ee #e4e8ee transparent transparent;

    display: flex;
    justify-content: center;
    align-items: center;

    &-svg {
      width: 42px;
      height: 42px;
      transform: translate(22px, -22px);
    }
  }

  &-top-right-label {
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
    margin-top: 16px;
    margin-right: -4px;
    justify-content: center;
    align-items: center;

    img {
      height: 24px;
      width: 100%;
      position: absolute;
    }

    &-content {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #fff;
      transform: translate(3px, 0px);
      padding: 2px 10px 2px 11px;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
